import { useEffect, useRef, useState } from "react";
import { X } from "lucide-react";
import { Avatar } from "./Avatar";
import { STATUSER, PRIORITETER, datoOm } from "./sakerUtils";

const chipKl = (aktiv) =>
  `inline-flex items-center gap-1.5 text-[12px] font-medium rounded-lg px-2.5 py-1.5 border transition-colors ${
    aktiv ? "border-neutral-900 bg-neutral-900 text-white" : "border-[#E7E4DB] bg-white text-neutral-600 hover:border-neutral-400"
  }`;

const Seksjon = ({ label, children }) => (
  <div className="mt-4">
    <p className="text-[10.5px] font-semibold uppercase tracking-wider text-neutral-400 mb-1.5">{label}</p>
    {children}
  </div>
);

const HURTIGFRISTER = [
  { l: "I dag", d: 0 },
  { l: "I morgen", d: 1 },
  { l: "Neste uke", d: 7 },
];

export default function NySakModal({ forvalgtStatus, members, onLagre, onLukk }) {
  const [form, setForm] = useState({
    title: "",
    description: "",
    status: forvalgtStatus || "inbox",
    priority: 2,
    assigneeId: "",
    dueDate: "",
  });
  const [lagrer, setLagrer] = useState(false);
  const tittelRef = useRef(null);

  useEffect(() => { tittelRef.current?.focus(); }, []);
  useEffect(() => {
    const esc = (e) => { if (e.key === "Escape") onLukk(); };
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [onLukk]);

  const sett = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const lagre = async () => {
    if (!form.title.trim() || lagrer) return;
    setLagrer(true);
    try {
      await onLagre({
        ...form,
        title: form.title.trim(),
        assigneeId: form.assigneeId || null,
        dueDate: form.dueDate || null,
      });
    } finally {
      setLagrer(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-[8vh] sm:pt-[12vh] px-4" data-testid="ny-sak-modal">
      <div className="sak-backdrop absolute inset-0 bg-black/25" onClick={onLukk} />
      <div className="sak-modal relative w-full max-w-[580px] max-h-[84vh] overflow-y-auto bg-white rounded-2xl shadow-2xl border border-[#E7E4DB]">
        <div className="flex items-center justify-between px-5 pt-4">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-neutral-400">Ny sak</span>
          <button type="button" onClick={onLukk} className="text-neutral-400 hover:text-neutral-800 transition-colors" data-testid="ny-sak-lukk">
            <X size={16} />
          </button>
        </div>
        <div className="px-5 pt-2 pb-5">
          <input
            ref={tittelRef}
            value={form.title}
            onChange={(e) => sett("title", e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) lagre(); }}
            maxLength={300}
            placeholder="Hva gjelder saken?"
            className="w-full bg-transparent text-[17px] font-semibold text-neutral-900 focus:outline-none placeholder:text-neutral-300 py-1"
            data-testid="ny-sak-tittel"
          />
          <textarea
            value={form.description}
            onChange={(e) => sett("description", e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) lagre(); }}
            rows={3}
            maxLength={8000}
            placeholder="Beskrivelse (valgfritt, Markdown støttes)"
            className="w-full text-[13px] leading-relaxed text-neutral-700 focus:outline-none resize-none placeholder:text-neutral-300 mt-1"
            data-testid="ny-sak-beskrivelse"
          />

          <Seksjon label="Status">
            <div className="flex flex-wrap gap-1.5" data-testid="ny-sak-status">
              {STATUSER.map((s) => (
                <button
                  key={s.k}
                  type="button"
                  onClick={() => sett("status", s.k)}
                  className={chipKl(form.status === s.k)}
                  data-testid={`ny-sak-status-${s.k}`}
                >
                  <span className="w-1.5 h-1.5 rounded-full" style={{ background: s.dot }} />
                  {s.label}
                </button>
              ))}
            </div>
          </Seksjon>

          <Seksjon label="Prioritet">
            <div className="flex flex-wrap gap-1.5" data-testid="ny-sak-prioritet">
              {PRIORITETER.map((p) => (
                <button
                  key={p.v}
                  type="button"
                  onClick={() => sett("priority", p.v)}
                  className={chipKl(form.priority === p.v)}
                  data-testid={`ny-sak-pri-${p.v}`}
                >
                  <span className="bud-mono text-[10.5px] font-bold" style={form.priority === p.v ? {} : { color: p.farge }}>{p.kort}</span>
                  {p.label.split("· ")[1]}
                </button>
              ))}
            </div>
          </Seksjon>

          <Seksjon label="Ansvarlig">
            <div className="flex flex-wrap gap-1.5" data-testid="ny-sak-ansvarlig">
              <button
                type="button"
                onClick={() => sett("assigneeId", "")}
                className={chipKl(!form.assigneeId)}
                data-testid="ny-sak-ansvarlig-ingen"
              >
                Ingen
              </button>
              {members.map((m) => (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => sett("assigneeId", form.assigneeId === m.id ? "" : m.id)}
                  className={chipKl(form.assigneeId === m.id)}
                  data-testid={`ny-sak-ansvarlig-${m.id}`}
                >
                  <Avatar person={m} size={17} />
                  {m.name.split(" ")[0]}
                </button>
              ))}
            </div>
          </Seksjon>

          <Seksjon label="Frist">
            <div className="flex flex-wrap items-center gap-1.5">
              {HURTIGFRISTER.map((h) => {
                const dato = datoOm(h.d);
                const aktiv = form.dueDate === dato;
                return (
                  <button
                    key={h.l}
                    type="button"
                    onClick={() => sett("dueDate", aktiv ? "" : dato)}
                    className={chipKl(aktiv)}
                    data-testid={`ny-sak-frist-hurtig-${h.d}`}
                  >
                    {h.l}
                  </button>
                );
              })}
              <input
                type="date"
                value={form.dueDate}
                onChange={(e) => sett("dueDate", e.target.value)}
                className="text-[12px] border border-[#E7E4DB] rounded-lg px-2.5 py-[5px] bg-white text-neutral-600 focus:outline-none focus:border-neutral-400 transition-colors"
                data-testid="ny-sak-frist"
              />
            </div>
          </Seksjon>

          <div className="flex items-center justify-between mt-6">
            <span className="hidden sm:inline text-[10.5px] text-neutral-300">Cmd/Ctrl + Enter for å lagre</span>
            <div className="flex items-center gap-2 ml-auto">
              <button type="button" onClick={onLukk} className="text-[12.5px] text-neutral-500 px-3 py-2 hover:text-neutral-800 transition-colors">
                Avbryt
              </button>
              <button
                type="button"
                onClick={lagre}
                disabled={!form.title.trim() || lagrer}
                className="text-[12.5px] font-medium bg-neutral-900 text-white px-4 py-2 rounded-lg hover:bg-neutral-700 disabled:opacity-30 transition-all"
                data-testid="ny-sak-lagre"
              >
                {lagrer ? "Oppretter …" : "Opprett sak"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
